/**
 * Group select - pick existing or type custom
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useState, useMemo, useEffect } from 'react';

import { Input } from '@/components/ui';

export interface GroupSelectProps {
  value: string;
  onChange: (value: string) => void;
  availableGroupNames: string[];
  placeholder?: string;
  className?: string;
}

function deriveMode(value: string, list: string[]): 'none' | 'existing' | 'custom' {
  if (!value.trim()) return 'none';
  if (list.includes(value.trim())) return 'existing';
  return 'custom';
}

export const GroupSelect: FC<GroupSelectProps> = ({
  value,
  onChange,
  availableGroupNames,
  placeholder = 'e.g. March 2026',
  className,
}) => {
  const derived = useMemo(() => deriveMode(value, availableGroupNames), [value, availableGroupNames]);
  const [mode, setMode] = useState<'none' | 'existing' | 'custom'>(derived);
  useEffect(() => setMode(derived), [derived]);

  const handleModeChange = (m: 'none' | 'existing' | 'custom') => {
    setMode(m);
    if (m === 'none') onChange('');
  };

  if (availableGroupNames.length === 0) {
    return (
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={80}
        className={className ?? 'h-9'}
      />
    );
  }

  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <select
        value={mode}
        onChange={(e) => handleModeChange(e.target.value as 'none' | 'existing' | 'custom')}
        className={`h-9 shrink-0 rounded-md border border-input bg-background px-2 text-sm ${className ?? ''}`.trim()}
      >
        <option value="none">— None —</option>
        <option value="existing">Pick existing</option>
        <option value="custom">Custom</option>
      </select>
      {mode === 'existing' && (
        <select
          value={value.trim()}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">Select group</option>
          {availableGroupNames.map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </select>
      )}
      {mode === 'custom' && (
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={80}
          className="h-9 min-w-0 flex-1"
        />
      )}
    </div>
  );
};
